import React, { useState, useEffect, useRef } from "react";
import RegisterPage1 from "./RegisterPage1";
import RegisterPage2 from "./RegisterPage2";
import RegisterPage3 from "./RegisterPage3";
import RegisterPage4 from "./RegisterPage4";

const RegisterForm = ({ 
  student_id = null, 
  initialData = null, 
  onSubmit = null,
  onNext = null,
  onBack = null,
  onDataChange = null,
  isEmbedded = false
}) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(!!student_id);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // refs to track state between renders
  const hasNotifiedInitialData = useRef(false);
  const previousFormDataRef = useRef({});

  // Initialize form data with initialData
  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData }));
    }
    setIsLoading(false);
  }, [initialData]);

  // Notify parent when formData changes (loop-proof)
  useEffect(() => {
    if (!onDataChange || Object.keys(formData).length === 0) return;

    const formDataString = JSON.stringify(formData);
    const previousDataString = JSON.stringify(previousFormDataRef.current);

    if (formDataString !== previousDataString) {
      // avoid firing immediately with unchanged initialData
      if (
        hasNotifiedInitialData.current ||
        !initialData ||
        formDataString !== JSON.stringify(initialData)
      ) {
        onDataChange(formData);
        hasNotifiedInitialData.current = true;
      }
      previousFormDataRef.current = formData;
    }
  }, [formData, initialData]); // ✅ FIXED: Removed onDataChange to prevent infinite loop

  const handleNext = (data) => {
    const updatedData = { ...formData, ...data };
    setFormData(updatedData);

    if (step === 4) {
      if (onSubmit) {
        onSubmit(updatedData);
      } else {
        handleFinalSubmit(updatedData);
      }
      return;
    }

    setStep((prev) => prev + 1);
    if (onNext) onNext(updatedData, step + 1);
  };

  const handlePrevious = () => {
    if (step > 1) setStep((prev) => prev - 1);
    if (onBack) onBack(step - 1);
  };

  const handleStepDataChange = (stepData) => {
    setFormData((prev) => ({ ...prev, ...stepData }));
  };

  const handleFinalSubmit = async (data) => {
    const finalData = { ...formData, ...data };
    setIsSubmitting(true);

    try {
      let response;

      if (student_id) {
        // update existing student
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/update-student-profile/${student_id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(finalData),
          }
        );
        if (!response.ok) throw new Error("Failed to update student profile");
        await response.json();
        alert("Student profile updated successfully!");
      } else {
        // new registration
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/create-student-profile`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(finalData),
          }
        );
        if (!response.ok) throw new Error("Failed to create student profile");
        await response.json();
        alert("Registration completed successfully!");
        localStorage.removeItem("registrationFormData");
      }

      if (onSubmit) onSubmit(finalData);
    } catch (err) {
      console.error("Error submitting form:", err);
      alert(`Error ${student_id ? "updating" : "creating"} profile: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Progress Steps Component

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="text-gray-600">Loading form data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-10">
      
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 sm:p-6 lg:p-8">
            {step === 1 && (
              <RegisterPage1
                student_id={student_id}
                initialData={initialData}
                formData={formData}
                onNext={handleNext}
                onDataChange={handleStepDataChange}
                isSubmitting={isSubmitting}
              />
            )}
            {step === 2 && (
              <RegisterPage2
                student_id={student_id}
                initialData={initialData}
                formData={formData}
                onSubmit={handleNext}
                onPrevious={handlePrevious}
                onDataChange={handleStepDataChange}
                isSubmitting={isSubmitting}
              />
            )}
            {step === 3 && (
              <RegisterPage3
                student_id={student_id}
                initialData={initialData}
                formData={formData}
                onSubmit={handleNext}
                onPrevious={handlePrevious}
                onDataChange={handleStepDataChange}
                isSubmitting={isSubmitting}
              />
            )}
            {step === 4 && (
              <RegisterPage4
                student_id={student_id}
                initialData={initialData}
                formData={formData}
                onSubmit={handleNext}
                onPrevious={handlePrevious}
                onDataChange={handleStepDataChange}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;